import { ReactElement, ReactNode, useState } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { Button } from '../../../tailwindComponents/Button';

export interface StageSummaryRow {
  value: ReactNode;
  label: string;
}

export function StageSummaryGrid({ rows }: { rows: StageSummaryRow[] }) {
  return (
    <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
      {rows.map((row) => (
        <div key={row.label}>
          <p className='text-sm font-semibold text-gray-900'>{row.value}</p>
          <p className='text-xs text-gray-400'>{row.label}</p>
        </div>
      ))}
    </div>
  );
}

export interface StageView {
  label: string;
  onView: () => void;
}

export type StageTone = 'success' | 'warning' | 'danger';

export interface FulfilmentStage {
  key: string;
  pendingLabel: string;
  doneLabel: string;
  isComplete: boolean;
  doneTone?: StageTone;
  summary?: ReactNode;
  view?: StageView;
  action?: {
    label: string;
    onAct: () => void;
    intent?: 'primary' | 'secondary';
    startIcon?: ReactElement;
  };
  note?: string;
}

export function terminalTone(
  isRejected: boolean,
  hasDiscrepancy: boolean
): StageTone | undefined {
  if (isRejected) return 'danger';
  if (hasDiscrepancy) return 'warning';
  return undefined;
}

export function withMonotonicCompletion(
  stages: FulfilmentStage[]
): FulfilmentStage[] {
  let laterComplete = false;
  return stages
    .slice()
    .reverse()
    .map((stage) => {
      if (stage.isComplete) laterComplete = true;
      const isComplete = stage.isComplete || laterComplete;
      return isComplete === stage.isComplete ? stage : { ...stage, isComplete };
    })
    .reverse();
}

export function latestStageView(
  stages: FulfilmentStage[]
): StageView | undefined {
  const currentIndex = stages.findIndex((stage) => !stage.isComplete);
  for (let index = stages.length - 1; index >= 0; index -= 1) {
    const stage = stages[index];
    if (stage.view && (stage.isComplete || index === currentIndex)) {
      return stage.view;
    }
  }
  return undefined;
}

function DoneMarker({ tone }: { tone: StageTone }) {
  if (tone === 'danger') {
    return <XCircleIcon className='w-5 h-5 text-red-600' aria-hidden='true' />;
  }
  const color = tone === 'warning' ? 'text-amber-500' : 'text-green-600';
  return <CheckCircleIcon className={`w-5 h-5 ${color}`} aria-hidden='true' />;
}

function DoneStage({
  stage,
  isExpanded,
  onToggle,
}: {
  stage: FulfilmentStage;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const expandable = !!stage.summary || !!stage.view;
  const tone = stage.doneTone ?? 'success';

  return (
    <div className='border border-gray-200 rounded-lg overflow-hidden'>
      {expandable ? (
        <button
          type='button'
          onClick={onToggle}
          aria-expanded={isExpanded}
          className='w-full flex items-center justify-between p-4 bg-gray-100 hover:bg-gray-200 transition-colors'
        >
          <div className='flex items-center gap-3'>
            <DoneMarker tone={tone} />
            <span className='text-sm font-medium text-gray-900'>
              {stage.doneLabel}
            </span>
          </div>
          <ChevronDownIcon
            className={`w-5 h-5 text-gray-500 transition-transform ${
              isExpanded ? 'rotate-180' : ''
            }`}
          />
        </button>
      ) : (
        <div className='flex items-center gap-3 p-4 bg-gray-100'>
          <DoneMarker tone={tone} />
          <span className='text-sm font-medium text-gray-900'>
            {stage.doneLabel}
          </span>
        </div>
      )}
      {expandable && isExpanded && (
        <div className='p-4 bg-white border-t border-gray-200'>
          {stage.summary}
          {stage.view && (
            <div className={stage.summary ? 'mt-4' : ''}>
              <Button
                label={stage.view.label}
                intent='primary'
                size='sm'
                className='w-full'
                startIcon={<MagnifyingGlassIcon className='w-4 h-4' />}
                onClick={stage.view.onView}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CurrentStage({
  stage,
  number,
}: {
  stage: FulfilmentStage;
  number: number;
}) {
  const hasBody = !!stage.action || !!stage.note;

  return (
    <div className='border border-primary/40 bg-blue-50 rounded-lg p-4'>
      <div className='flex items-center gap-3'>
        <span className='w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold flex-shrink-0'>
          {number}
        </span>
        <span className='text-sm font-medium text-primary'>
          {stage.pendingLabel}
        </span>
      </div>
      {hasBody && (
        <div className='mt-3 space-y-2'>
          {stage.action && (
            <Button
              label={stage.action.label}
              intent={stage.action.intent ?? 'primary'}
              size='sm'
              className='w-full'
              startIcon={stage.action.startIcon}
              onClick={stage.action.onAct}
            />
          )}
          {stage.note && (
            <p className='text-xs text-gray-400 text-center'>{stage.note}</p>
          )}
        </div>
      )}
    </div>
  );
}

function UpcomingStage({
  stage,
  number,
}: {
  stage: FulfilmentStage;
  number: number;
}) {
  return (
    <div className='border border-dashed border-gray-200 rounded-lg p-4'>
      <div className='flex items-center gap-3'>
        <span className='w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center text-xs text-gray-500 flex-shrink-0'>
          {number}
        </span>
        <span className='text-sm text-gray-500'>{stage.pendingLabel}</span>
      </div>
    </div>
  );
}

interface FulfilmentStageTrackerProps {
  stages: FulfilmentStage[];
  initialExpandedKey?: string | null;
  onCollapse?: () => void;
}

export function FulfilmentStageTracker({
  stages,
  initialExpandedKey = null,
  onCollapse,
}: FulfilmentStageTrackerProps) {
  const [expandedKey, setExpandedKey] = useState<string | null>(
    initialExpandedKey
  );

  const currentIndex = stages.findIndex((stage) => !stage.isComplete);

  const toggle = (key: string) => {
    setExpandedKey((previous) => {
      const next = previous === key ? null : key;
      if (next === null) onCollapse?.();
      return next;
    });
  };

  return (
    <div className='mb-6'>
      <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
        Fulfilment Progress
      </p>
      <div className='space-y-2'>
        {stages.map((stage, index) => {
          if (stage.isComplete) {
            return (
              <DoneStage
                key={stage.key}
                stage={stage}
                isExpanded={expandedKey === stage.key}
                onToggle={() => toggle(stage.key)}
              />
            );
          }
          if (index === currentIndex) {
            return (
              <CurrentStage key={stage.key} stage={stage} number={index + 1} />
            );
          }
          return (
            <UpcomingStage key={stage.key} stage={stage} number={index + 1} />
          );
        })}
      </div>
    </div>
  );
}
